<template>
  <div class="user-site-page">
    <section class="page-hero">
      <div class="d-flex align-items-start justify-content-between flex-wrap gap-12">
        <div class="hero-title">
          <div class="d-flex align-items-center">
            <div class="hero-icon">
              <feather-icon
                icon="UsersIcon"
                size="18"
                class="text-white"
              />
            </div>
            <div class="ml-1">
              <h2 class="mb-0">
                Site Engineers
              </h2>
              <p class="mb-0 text-muted">
                Manage accounts, access, QR codes, and performance insights.
              </p>
            </div>
          </div>
        </div>

        <div class="d-flex align-items-center flex-wrap gap-8">
          <b-button
            variant="outline-secondary"
            class="btn-soft-outline"
            @click="openJoinLink"
          >
            <feather-icon
              icon="ExternalLinkIcon"
              size="16"
              class="mr-50"
            />
            Open join link
          </b-button>
        </div>
      </div>

      <!-- steps section removed -->
    </section>

    <general-table
      class="mt-2"
      :api-url="apiUrl"
      :edit-component="editComponent"
      :delete-content="false"
      :block-content="false"
      :edit-content="true"
      :view-content="false"
      :filter-user-site="true"
      :columns="columns"
      :type="type"
      :need-user-excel="true"
      :have-sidebar="true"
      @handle-row-click="handleRowClick"
    >
      <template #cell(photo)="data">
        <div
          class="user-cell d-flex align-items-center"
        >
          <div
            class="cursor-pointer"
            @click="$router.push({ name: 'edit-user-site', params: { id: data.item.id, name: data.item.name } })"
          >
            <b-avatar
              variant="light"
              :src="data.item.photo"
              :text="avatarText(data.item.name)"
              size="48px"
              class="user-avatar"
            />
          </div>
          <div class="ml-1 flex-grow-1 min w-0">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex flex-column align-items-start">
                <div
                  class="user-name text-truncate cursor-pointer"
                  @click="$router.push({ name: 'edit-user-site', params: { id: data.item.id, name: data.item.name } })"
                >
                  {{ data.item.name }}
                </div>
                <div class="user-sub text-truncate">
                  <feather-icon
                    icon="MailIcon"
                    size="14"
                    class="mr-25 text-muted"
                  />
                  <span class="text-muted">{{ data.item.email }}</span>
                </div>
                <div class="user-sub text-truncate">
                  <feather-icon
                    icon="PhoneIcon"
                    size="14"
                    class="mr-25 text-muted"
                  />
                  <span class="text-muted">{{ data.item.phone }}</span>
                </div>
              </div>

              <div class="d-none d-md-flex align-items-center gap-6">
                <b-button
                  size="sm"
                  variant="flat-primary"
                  class="icon-btn"
                  @click.stop="$router.push({ name: 'edit-user-site', params: { id: data.item.id, name: data.item.name } })"
                >
                  <feather-icon
                    icon="Edit3Icon"
                    size="14"
                  />
                </b-button>

                <b-button
                  size="sm"
                  variant="flat-success"
                  class="icon-btn"
                  @click.stop="$router.push({ name: 'user-site-performance', params: { id: data.item.id } })"
                >
                  <feather-icon
                    icon="TrendingUpIcon"
                    size="14"
                  />
                </b-button>
              </div>
            </div>

            <div class="mt-50 d-flex align-items-center flex-wrap gap-6">
              <b-badge
                pill
                :variant="data.item.role === 'delegator' ? 'light-warning' : 'light-info'"
                class="badge-soft"
              >
                <feather-icon
                  icon="ShieldIcon"
                  size="12"
                  class="mr-25"
                />
                {{ data.item.role }}
              </b-badge>
            </div>
          </div>
        </div>
      </template>

      <template #cell(active)="data">
        <b-badge
          pill
          :variant="data.item.active == 1 ? 'light-success' : 'light-danger'"
          class="badge-soft"
        >
          <feather-icon
            :icon="data.item.active == 1 ? 'CheckCircleIcon' : 'XCircleIcon'"
            size="14"
            class="mr-25"
          />
          {{ resolveActiveUser(data.item.active) }}
        </b-badge>
      </template>

      <template #cell(QRCode)="data">
        <div class="d-flex align-items-center justify-content-center">
          <QrCode
            v-if="data.item.role !== 'delegator'"
            :item-id="data.item.token"
            :item-name="data.item.name"
          />
          <b-badge
            v-else
            pill
            variant="light-secondary"
            class="badge-soft"
          >
            <feather-icon
              icon="LockIcon"
              size="14"
              class="mr-25"
            />
            Not available
          </b-badge>
        </div>
      </template>

      <template #cell(performance)="data">
        <b-button
          size="sm"
          variant="outline-primary"
          class="perf-btn"
          @click.stop="$router.push({ name: 'user-site-performance', params: { id: data.item.id } })"
        >
          <feather-icon
            icon="BarChart2Icon"
            size="14"
            class="mr-50"
          />
          View
        </b-button>
      </template>
      <template #cell(Certifications)="data">
        <b-button
          size="sm"
          variant="outline-secondary"
          class="perf-btn"
          @click.stop="$router.push({ name: 'certificates-status-work', params: { id: data.item.id } })"
        >
          <feather-icon
            icon="ShieldIcon"
            size="14"
            class="mr-50"
          />
          certifications
        </b-button>
      </template>
    </general-table>

    <right-sidebar
      v-model="isSidebarOpen"
      :title="selectedRow ? `Details: ${selectedRow.name}` : 'Row Details'"
      width="600px"
      :show-tabs="true"
      :tabs="[
        { id: 'details', label: 'Details' },
        { id: 'information', label: 'Information' },
        { id: 'documents', label: 'Documents' }
      ]"
      @hide="handleSidebarClose"
    >
      <template #details>
        <user-site-details-tab
          v-if="selectedRow"
          :user="selectedRow"
        />
      </template>
      <template #information>
        <user-site-information-tab
          v-if="selectedRow"
          :user="selectedRow"
        />
      </template>
      <template
        v-if="canViewDocuments"
        #documents
      >
        <user-site-documents-tab
          v-if="selectedRow"
          :user="selectedRow"
        />
      </template>
    </right-sidebar>
  </div>
</template>

<script>
import GeneralTableUserSite from '@/components/general-table/GeneralTableUserSite.vue'
import QrCode from '@/components/general-table/QrCode.vue'
import RightSidebar from '@/components/RightSidebar.vue'
import UserSiteDetailsTab from './UserSiteDetailsTab.vue'
import UserSiteInformationTab from './UserSiteInformationTab.vue'
import UserSiteDocumentsTab from './UserSiteDocumentsTab.vue'

export default {
  components: {
    GeneralTable: GeneralTableUserSite,
    QrCode,
    RightSidebar,
    UserSiteDetailsTab,
    UserSiteInformationTab,
    UserSiteDocumentsTab,
  },
  data() {
    const canViewDocuments = ['super-manager', 'hr', 'hr-office', 'office-manager'].includes(
      localStorage.getItem('accessRole'),
    )

    return {
      APIURL: 'register/site',
      // addType: 'Add User Site',
      addComponentName: 'Add User',
      addUrlLink: 'https://contact.gulftic.com/join/gulftic',
      editComponent: 'edit-user-site',
      viewContent: true,
      type: 'page',
      partners: [],
      filter: {},
      types: '',
      selectedRow: null,
      isSidebarOpen: false,
      canViewDocuments,
      columns: [
        { key: 'photo', label: 'Information' },
        { key: 'active', label: 'Active' },
        // { key: 'role', label: 'Role' },
        { key: 'QRCode', label: 'QRCode' },
        { key: 'Certifications', label: 'certifications' },
        { key: 'performance', label: 'Performance Evaluation' },
        { key: 'actions' },
      ],
    }
  },

  computed: {
    apiUrl() {
      let base = 'register/site?'

      if (!Object.values(this.filter).length) {
        return base.slice(0, -1)
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Object.entries(this.filter)) {
        if (key && value) {
          base += `${key}=${value}&`
        }
      }
      // console.log(base.slice(0, -1))
      return base.slice(0, -1)
    },
  },
  mounted() {
  },
  setup() {
    const resolveActiveUser = role => {
      if (role === 1) return 'Active'

      return 'Not Active'
    }
    return {
      resolveActiveUser,
    }
  },

  methods: {
    openJoinLink() {
      window.open(this.addUrlLink, '_blank', 'noopener,noreferrer')
    },
    avatarText(name) {
      if (!name) return ''
      const parts = String(name).trim().split(/\s+/).filter(Boolean)
      const first = parts[0]?.[0] || ''
      const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : ''
      return (first + last).toUpperCase()
    },

    filterTypes(type) {
      const filteredType = this.status.filter(item => item.text !== type)
      this.types = filteredType
    },
    handleRowClick(item) {
      this.selectedRow = item
      this.isSidebarOpen = true
    },
    handleSidebarClose() {
      this.isSidebarOpen = false
      this.selectedRow = null
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/pages/user-site/user-site.scss"></style>
<style lang="scss" src="@/assets/scss/pages/user-site/general-table-user-site.scss"></style>
<style lang="scss" src="@/assets/scss/pages/user-site/filter-user-user-site.scss"></style>
